import { supabase } from '../supabase';
import { Recommendation } from '../../types';

// Função auxiliar para gerar UUIDs de maneira compatível com browser e node
// Exportar para poder ser usada externamente
export function generateUUID(): string {
  // Use crypto.randomUUID() se disponível (moderno) ou fallback para uma implementação manual
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  } else {
    // Implementação simples de UUID v4 para fallback
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }
}

/**
 * Serviço para armazenar recomendações no banco de dados
 */
export class RecommendationStorage {
  /**
   * Salva recomendações no banco de dados usando upsert para eficiência.
   */
  public async saveRecommendations(recommendations: Recommendation[]): Promise<void> {
    if (!recommendations || recommendations.length === 0) {
      console.log('Nenhuma recomendação para salvar');
      return;
    }

    try {
      console.log(`Iniciando salvamento de ${recommendations.length} recomendações`);

      // Preparar dados para upsert no banco
      const recsToUpsert = recommendations.map(rec => {
        // Formatar game como JSON para armazenamento seguro
        const game = rec.game ? this.cleanGameObjectForStorage(rec.game) : null;
        const now = new Date().toISOString();

        // ID é necessário para o upsert inicial, mas pode ser nulo ou indefinido se for uma nova rec.
        // O banco gerará um se não existir, mas o upsert precisa saber o que fazer em conflito (user_id, game_id).
        // Se um ID fixo for passado e já existir *mas* com outro user/game, pode causar erro inesperado.
        // É mais seguro deixar o ID ser gerenciado pelo banco (se novo) ou usar o ID existente (se update).
        // No entanto, `upsert` do supabase precisa de todas as colunas.
        // Vamos garantir que um ID exista, mesmo que temporário, e o `onConflict` cuidará da lógica.
        const id = rec.id || generateUUID(); // Garante um ID para o upsert

        return {
          id: id, // Necessário para o upsert inicial
          user_id: rec.user_id,
          game_id: rec.game_id,
          score: rec.score,
          reason: rec.reason || '',
          algorithm: rec.algorithm,
          viewed: rec.viewed || false,
          liked: rec.liked, // Pode ser null
          game: game, // Armazenar o objeto do jogo como JSON
          // created_at é gerenciado pelo banco no insert
          updated_at: now // Sempre atualiza no upsert
        };
      });

      console.log(`${recsToUpsert.length} recomendações preparadas para upsert.`);

      // Dividir em lotes menores para evitar erros de tamanho de payload
      const BATCH_SIZE = 50; // Aumentado um pouco, teste conforme necessário

      for (let i = 0; i < recsToUpsert.length; i += BATCH_SIZE) {
        const batch = recsToUpsert.slice(i, i + BATCH_SIZE);
        console.log(`Processando lote ${i / BATCH_SIZE + 1} com ${batch.length} recomendações.`);

        try {
          const { error } = await supabase
            .from('recommendations')
            .upsert(batch, {
              // Conflito na constraint UNIQUE (user_id, game_id)
              onConflict: 'user_id, game_id',
              // Não ignore duplicatas, atualize a linha existente.
              // Isso vai inserir se não existir, ou atualizar se existir.
              ignoreDuplicates: false,
              // Supabase JS v2 infere a coluna de retorno, mas podemos ser explícitos se necessário
              // select: 'id' // Opcional: retornar o ID
            });

          if (error) {
            console.error(`Erro ao fazer upsert do lote de recomendações (${i} a ${i + batch.length - 1}):`, error);
            // Tentar salvar individualmente em caso de erro no lote
            console.warn(`Tentando salvar recomendações do lote ${i / BATCH_SIZE + 1} individualmente...`);
            await this.saveRecommendationsIndividually(batch); // Passa o lote que falhou
          } else {
            console.log(`Lote ${i / BATCH_SIZE + 1} processado com sucesso.`);
          }
        } catch (batchError) {
          console.error(`Erro crítico no processamento do lote ${i / BATCH_SIZE + 1}:`, batchError);
           // Tentar salvar individualmente em caso de erro crítico no lote
           console.warn(`Tentando salvar recomendações do lote ${i / BATCH_SIZE + 1} individualmente devido a erro crítico...`);
           await this.saveRecommendationsIndividually(batch); // Passa o lote que falhou
        }
      }

      console.log(`${recsToUpsert.length} recomendações processadas.`);

    } catch (error) {
      console.error('Erro geral ao preparar ou iniciar o salvamento de recomendações:', error);
      // Fallback final para salvar individualmente se a preparação falhar
      await this.saveRecommendationsIndividually(recommendations);
    }
  }

  /**
   * Limpa um objeto de jogo para armazenamento seguro como JSONB.
   * Remove propriedades potencialmente problemáticas ou muito grandes.
   */
  private cleanGameObjectForStorage(game: any): any {
    if (!game) return null;

    // Cria uma cópia superficial para não modificar o original
    const cleanedGame = { ...game };

    // Remover campos grandes ou potencialmente circulares que não são essenciais para a exibição na recomendação
    // Adapte conforme a estrutura real do seu objeto `Game` e o que você precisa armazenar.
    delete cleanedGame.embedding; // Exemplo: Remover embedding se existir
    delete cleanedGame.complexCircularReference; // Exemplo
    delete cleanedGame.largeDescriptionNotNeeded; // Exemplo

    // Garantir que tipos complexos sejam serializáveis
    // Ex: Converter Date para ISOString se necessário (embora JSON.stringify faça isso)
    Object.keys(cleanedGame).forEach(key => {
      if (cleanedGame[key] instanceof Date) {
        cleanedGame[key] = cleanedGame[key].toISOString();
      }
      // Adicione outras conversões ou limpezas conforme necessário
    });

    // Limitar o tamanho da string JSON (opcional, como fallback)
    try {
      const jsonString = JSON.stringify(cleanedGame);
      // Defina um limite de tamanho razoável, se necessário
      // const MAX_SIZE_KB = 10;
      // if (new TextEncoder().encode(jsonString).length > MAX_SIZE_KB * 1024) {
      //   console.warn(`Game object JSON for game_id ${game.id} too large, storing only ID and name.`);
      //   return JSON.stringify({ id: game.id, name: game.name });
      // }
      return JSON.parse(jsonString); // Retorna o objeto limpo
    } catch (error) {
      console.error(`Erro ao serializar game object para game_id ${game?.id}:`, error);
      // Retornar um objeto mínimo em caso de erro de serialização
      return { id: game?.id, name: game?.name, error: 'Serialization failed' };
    }
  }

  /**
   * Salva recomendações individualmente no banco de dados (fallback).
   * Usa upsert para consistência com o método principal.
   */
  private async saveRecommendationsIndividually(recommendations: Recommendation[] | any[]): Promise<void> {
    if (!recommendations || recommendations.length === 0) {
      return;
    }
    console.warn(`Iniciando salvamento individual para ${recommendations.length} recomendações (fallback).`);

    for (const rec of recommendations) {
      try {
        // Preparar dados como no método principal
        const game = rec.game ? this.cleanGameObjectForStorage(rec.game) : null;
        const now = new Date().toISOString();
        const id = rec.id || generateUUID(); // Garante um ID

        const recToUpsert = {
          id: id,
          user_id: rec.user_id,
          game_id: rec.game_id,
          score: rec.score,
          reason: rec.reason || '',
          algorithm: rec.algorithm,
          viewed: rec.viewed || false,
          liked: rec.liked,
          game: game,
          updated_at: now
        };

        const { error } = await supabase
          .from('recommendations')
          .upsert(recToUpsert, {
            onConflict: 'user_id, game_id',
            ignoreDuplicates: false,
          });

        if (error) {
          console.error(`Erro ao salvar recomendação individual (${rec.user_id}, ${rec.game_id}):`, error);
        } else {
           // console.log(`Recomendação individual (${rec.user_id}, ${rec.game_id}) salva/atualizada com sucesso.`);
        }
      } catch (error) {
        console.error(`Erro crítico ao processar recomendação individual (${rec?.user_id}, ${rec?.game_id}):`, error);
      }
    }
     console.warn(`Salvamento individual concluído para ${recommendations.length} recomendações.`);
  }
  
  /**
   * Obtém recomendações existentes do banco de dados
   */
  public async getExistingRecommendations(userId: string, limit: number = 5): Promise<Recommendation[]> {
    try {
      const { data, error } = await supabase
        .from('recommendations')
        .select('*')  // Não precisamos mais fazer join com games, já temos o objeto game
        .eq('user_id', userId)
        .is('liked', null)
        .order('score', { ascending: false })
        .limit(limit);
        
      if (error) {
        console.error('Erro ao buscar recomendações existentes:', error);
        return [];
      }
      
      // Filtrar apenas recomendações com dados válidos do jogo
      const validRecs = (data || []).filter(rec => rec && (rec.game || rec.game_id));
      
      if (validRecs.length < (data || []).length) {
        console.warn(`Filtradas ${(data || []).length - validRecs.length} recomendações sem dados válidos do jogo`);
      }
      
      return validRecs as Recommendation[];
    } catch (error) {
      console.error('Erro ao processar recomendações existentes:', error);
      return [];
    }
  }
  
  /**
   * Marca uma recomendação específica como visualizada no banco de dados.
   * @param recommendationId O ID da recomendação a ser marcada como visualizada.
   * @returns Uma Promise que resolve quando a operação é concluída.
   * @throws Erro se a atualização no Supabase falhar.
   */
  async markRecommendationAsViewed(recommendationId: string): Promise<void> {
    if (!recommendationId) {
      console.warn('[Storage] markRecommendationAsViewed: recommendationId não fornecido.');
      return; // Simplesmente retorna se não há ID
    }
    try {
      const { error } = await supabase
        .from('recommendations')
        .update({ viewed: true, updated_at: new Date().toISOString() })
        .eq('id', recommendationId);

      if (error) {
        console.error(`[Storage] Erro ao marcar recomendação ${recommendationId} como visualizada:`, error);
        // Lançar um erro específico ou o erro do Supabase para tratamento pelo chamador
        throw new Error(`Falha ao marcar recomendação ${recommendationId} como visualizada: ${error.message}`);
      }
      // Não é necessário retornar nada em caso de sucesso, a ausência de erro indica sucesso.
      console.log(`[Storage] Recomendação ${recommendationId} marcada como visualizada com sucesso.`);
    } catch (err) {
      // Se err já é um Error, relançá-lo. Caso contrário, encapsulá-lo.
      if (err instanceof Error) {
        console.error(`[Storage] Exceção ao marcar recomendação ${recommendationId} como visualizada:`, err.message);
        throw err;
      } else {
        console.error(`[Storage] Exceção desconhecida ao marcar recomendação ${recommendationId} como visualizada:`, err);
        throw new Error(`Exceção desconhecida ao marcar recomendação ${recommendationId} como visualizada.`);
      }
    }
  }
  
  /**
   * Atualiza as recomendações em lote no banco de dados
   * Usado para registrar visualizações ou feedback em massa
   */
  public async updateBatchFeedback(
    recommendationIds: string[], 
    liked: boolean,
    userId: string
  ): Promise<boolean> {
    if (!recommendationIds || recommendationIds.length === 0) {
      console.log('Nenhuma recomendação para atualizar');
      return false;
    }
    
    try {
      // Atualizar cada recomendação individualmente para maior segurança
      let successCount = 0;
      
      for (const recId of recommendationIds) {
        const updated = await this.updateRecommendationFeedback(recId, liked, userId);
        if (updated) {
          successCount++;
        }
      }
      
      console.log(`${successCount} de ${recommendationIds.length} recomendações atualizadas com feedback`);
      return successCount > 0;
    } catch (error) {
      console.error('Erro ao atualizar lote de recomendações:', error);
      return false;
    }
  }
  
  /**
   * Marca uma recomendação como aceita (liked) ou rejeitada (disliked)
   * @returns O objeto Recommendation atualizado em caso de sucesso, ou null em caso de falha.
   */
  public async updateRecommendationFeedback(
    recommendationId: string, 
    liked: boolean | null, // Permitir null para remover feedback
    userId?: string // userId pode ser útil para RLS mais granular no futuro
  ): Promise<Recommendation | null> { // Mudar tipo de retorno
    try {
      // 1. Atualizar a recomendação
      const { error: updateError, count } = await supabase
        .from('recommendations')
        .update({ 
          liked: liked,
          updated_at: new Date().toISOString() 
        })
        .eq('id', recommendationId);
        // Remover o .select() daqui

      if (updateError) {
        console.error(`Erro ao ATUALIZAR feedback para recomendação ${recommendationId}:`, updateError);
        return null; // Falha explícita em caso de erro do Supabase
      }

      if (count === 0) {
          console.warn(`Recomendação com ID ${recommendationId} não encontrada para atualização de feedback.`);
          return null; // Falha explícita se a recomendação não existe
      }

      // 2. Buscar o registro atualizado APÓS o update bem-sucedido
      const { data: updatedData, error: selectError } = await supabase
        .from('recommendations')
        .select('*') // Buscar todos os campos necessários para o tipo Recommendation
        .eq('id', recommendationId)
        .single(); // Esperamos exatamente um registro

      if (selectError) {
        console.error(`Erro ao BUSCAR feedback atualizado para recomendação ${recommendationId}:`, selectError);
        // Mesmo que o update tenha funcionado, não conseguimos confirmar o estado final
        return null; 
      }

      console.log(`Feedback para recomendação ${recommendationId} atualizado e verificado com sucesso.`);
      // Retornar o objeto Recommendation completo
      // Assumindo que a estrutura de 'updatedData' corresponde à 'Recommendation'
      // Pode ser necessário um mapeamento se os nomes das colunas forem diferentes
      return updatedData as Recommendation; 

    } catch (err) {
      console.error(`Erro inesperado ao atualizar feedback para recomendação ${recommendationId}:`, err);
      return null; // Falha explícita em caso de erro inesperado
    }
  }
  
  /**
   * Obtém uma recomendação específica pelo seu ID
   * @param recommendationId ID da recomendação a ser buscada
   * @returns A recomendação encontrada ou null se não existir
   */
  public async getRecommendationById(recommendationId: string): Promise<Recommendation | null> {
    try {
      if (!recommendationId) {
        console.warn('Tentativa de buscar recomendação com ID vazio');
        return null;
      }

      const { data, error } = await supabase
        .from('recommendations')
        .select('*, games(*)')
        .eq('id', recommendationId)
        .maybeSingle();
        
      if (error) {
        console.error(`Erro ao buscar recomendação ${recommendationId}:`, error);
        return null;
      }
      
      if (!data) {
        console.warn(`Recomendação ${recommendationId} não encontrada`);
        return null;
      }

      // Converter os dados do banco para o formato da aplicação
      const recommendation: Recommendation = {
        id: data.id,
        user_id: data.user_id,
        game_id: data.game_id,
        score: data.score,
        reason: data.reason,
        algorithm: data.algorithm,
        viewed: data.viewed,
        liked: data.liked,
        // Se temos o objeto game na resposta, usamos ele; senão usamos o game armazenado no campo game
        game: data.games || data.game
      };
      
      return recommendation;
    } catch (error) {
      console.error('Erro ao buscar recomendação por ID:', error);
      return null;
    }
  }
  
  /**
   * Marca todas as recomendações em lote como visualizadas 
   */
  public async markBatchAsViewed(recommendationIds: string[], userId?: string): Promise<boolean> {
    if (!recommendationIds || recommendationIds.length === 0) {
      console.log('[Storage] Nenhuma recomendação para marcar como visualizada em lote');
      return false;
    }
    
    let successCount = 0;
    console.log(`[Storage] Iniciando marcação em lote como visualizadas para ${recommendationIds.length} recomendações.`);

    for (const recId of recommendationIds) {
      try {
        // userId não é mais passado para markRecommendationAsViewed, pois a versão atual não o utiliza.
        // Se for necessário no futuro, a assinatura de markRecommendationAsViewed precisará ser ajustada.
        await this.markRecommendationAsViewed(recId);
        successCount++;
      } catch (error) {
        console.warn(`[Storage] Falha ao marcar recomendação ${recId} como visualizada durante o lote:`, error);
        // Continuar com o próximo, não interromper o lote por uma falha individual.
      }
    }
    
    console.log(`[Storage] ${successCount} de ${recommendationIds.length} recomendações marcadas como visualizadas em lote.`);
    return successCount > 0; // Retorna true se pelo menos uma foi marcada com sucesso
  }
} 